<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
    @show="loadNotifications"
  >
    <template #button-content>
      <feather-icon
        :badge="unreadCount > 0 ? unreadCount : null"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">Notifications</h4>
        <b-badge v-if="unreadCount > 0" pill variant="light-primary">{{ unreadCount }} New</b-badge>
        <b-button v-if="unreadCount > 0" size="sm" variant="flat-primary" class="ml-1 p-0" @click="markAllRead">
          Mark all read
        </b-button>
      </div>
    </li>

    <vue-perfect-scrollbar
      :settings="{ maxScrollbarLength: 60, wheelPropagation: false }"
      class="scrollable-container media-list scroll-area"
      tagname="li"
      style="max-height: 320px;"
    >
      <div v-if="loading" class="text-center p-2">
        <b-spinner variant="primary" small />
      </div>
      <template v-else-if="notifications.length">
        <b-link
          v-for="n in notifications"
          :key="n.id"
          class="d-block"
          style="border-bottom: 1px solid #f0f0f0;"
          @click="markRead(n)"
        >
          <b-media class="p-1">
            <template #aside>
              <b-avatar size="32" :variant="n.read ? 'light-secondary' : 'light-primary'">
                <feather-icon icon="ShoppingCartIcon" />
              </b-avatar>
            </template>
            <p class="media-heading mb-0">
              <span :class="n.read ? '' : 'font-weight-bolder'">{{ n.title }}</span>
            </p>
            <small class="notification-text text-muted">{{ n.body }}</small>
          </b-media>
        </b-link>
      </template>
      <div v-else class="p-2 text-center text-muted small">No notifications</div>
    </vue-perfect-scrollbar>

    <li class="dropdown-menu-footer">
      <b-button variant="primary" block @click="$router.push({ name: 'apps-market-bookings' })">
        View All Bookings
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton, BSpinner,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Ripple from 'vue-ripple-directive'
import axios from '@/libs/axios'

const POLL_INTERVAL = 30000

export default {
  components: {
    BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton, BSpinner, VuePerfectScrollbar,
  },
  directives: { Ripple },
  data() {
    return {
      unreadCount: 0,
      notifications: [],
      loading: false,
      pollTimer: null,
    }
  },
  created() {
    this.pollUnread()
    this.pollTimer = setInterval(this.pollUnread, POLL_INTERVAL)
    // V7: Pause polling when tab is hidden to avoid unnecessary requests
    // (layout component never unmounts so beforeDestroy rarely fires)
    this._visibilityHandler = () => {
      if (document.hidden) {
        if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
      } else {
        this.pollUnread()
        this.pollTimer = setInterval(this.pollUnread, POLL_INTERVAL)
      }
    }
    document.addEventListener('visibilitychange', this._visibilityHandler)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    document.removeEventListener('visibilitychange', this._visibilityHandler)
  },
  methods: {
    async pollUnread() {
      try {
        const { data } = await axios.get('web/notifications/unread-count')
        this.unreadCount = data?.count ?? 0
      } catch {}
    },
    async loadNotifications() {
      this.loading = true
      try {
        const { data } = await axios.get('web/notifications')
        this.notifications = data?.data ?? data ?? []
      } finally {
        this.loading = false
      }
    },
    async markAllRead() {
      await axios.post('web/notifications/mark-all-read')
      // Vue 2: must use $set to trigger reactivity on existing object properties
      this.notifications.forEach(n => { this.$set(n, 'read', true) })
      this.unreadCount = 0
    },
    async markRead(n) {
      if (!n.read) {
        await axios.post(`web/notifications/${n.id}/read`)
        this.$set(n, 'read', true)
        if (this.unreadCount > 0) this.unreadCount--
      }
    },
  },
}
</script>

<style></style>
